import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { deals } from "./schema-deals.js";

// ============================================================================
// CONTROL CENTRE MODULE TABLES (7 tables) - All tables use cc_ prefix
// RESTRUCTURED: Project -> Execution Plan -> Tasks hierarchy
// ============================================================================

// 1. Projects Table (Top-level project containers)
export const projectTypeEnum = pgEnum("project_type", ["crm", "website", "marketing", "infrastructure", "business_operations"]);
export const projectStatusEnum = pgEnum("project_status", ["planning", "active", "on_hold", "completed", "cancelled"]);
export const projectPriorityEnum = pgEnum("project_priority", ["critical", "high", "medium", "low"]);

export const ccProjects = pgTable("crm_x_cc_projects", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  projectName: varchar("project_name", { length: 255 }).notNull(),
  projectDescription: text("project_description"),
  projectType: projectTypeEnum("project_type").notNull(),
  status: projectStatusEnum("status").default("planning").notNull(),
  priority: projectPriorityEnum("priority").default("medium").notNull(),
  progressPercentage: integer("progress_percentage").default(0).notNull(),
  strategyDocument: text("strategy_document"),
  startDate: date("start_date"),
  targetEndDate: date("target_end_date"),
  actualEndDate: date("actual_end_date"),
  owner: varchar("owner", { length: 100 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 2. Execution Plans Table (Flexible time periods within projects - formerly Sprints)
export const executionPlanStatusEnum = pgEnum("execution_plan_status", ["planning", "active", "completed", "cancelled"]);

export const ccExecutionPlans = pgTable("crm_x_cc_execution_plans", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: uuid("project_id").references(() => ccProjects.id, { onDelete: "cascade" }).notNull(),
  planName: varchar("plan_name", { length: 255 }).notNull(),
  planGoal: text("plan_goal"),
  startDate: date("start_date").notNull(),
  endDate: date("end_date").notNull(),
  status: executionPlanStatusEnum("status").default("planning").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 3. Tasks Table (Project tasks with optional execution plan assignment)
export const taskStatusEnum = pgEnum("task_status", ["not_started", "in_progress", "blocked", "review", "completed"]);
export const taskPriorityEnum = pgEnum("task_priority", ["high", "medium", "low"]);
export const taskEffortEnum = pgEnum("task_effort", ["low", "medium", "high"]);

export const ccTasks = pgTable("crm_x_cc_tasks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: uuid("project_id").references(() => ccProjects.id, { onDelete: "cascade" }).notNull(),
  executionPlanId: uuid("execution_plan_id").references(() => ccExecutionPlans.id, { onDelete: "set null" }),
  taskTitle: varchar("task_title", { length: 500 }).notNull(),
  taskDescription: text("task_description"),
  status: taskStatusEnum("status").default("not_started").notNull(),
  priority: taskPriorityEnum("priority").default("medium").notNull(),
  effort: taskEffortEnum("effort").default("medium").notNull(),
  impactDescription: text("impact_description"),
  category: varchar("category", { length: 100 }), // Website, CRM, Marketing, Infrastructure, etc.
  assignedTo: uuid("assigned_to"),
  dueDate: date("due_date"),
  completedAt: timestamp("completed_at"),
  blockedReason: text("blocked_reason"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 4. Performance Metrics Table (Multi-source performance data)
export const metricSourceEnum = pgEnum("metric_source", ["lighthouse", "vercel", "sentry", "google_search_console"]);

export const ccPerformanceMetrics = pgTable("crm_x_cc_performance_metrics", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  source: metricSourceEnum("source").notNull(),
  metricName: varchar("metric_name", { length: 255 }).notNull(),
  metricValue: numeric("metric_value", { precision: 15, scale: 2 }).notNull(),
  metricUnit: varchar("metric_unit", { length: 50 }), // ms, score, count, percentage, etc.
  recordedAt: timestamp("recorded_at").defaultNow().notNull(),
  url: varchar("url", { length: 500 }),
  additionalData: text("additional_data"), // JSON string for flexible metadata
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// 5. Dashboard Configurations Table (User-specific dashboard settings)
export const ccDashboardConfigurations = pgTable("crm_x_cc_dashboard_configurations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: uuid("user_id").notNull(),
  dashboardName: varchar("dashboard_name", { length: 255 }).notNull(),
  configuration: text("configuration").notNull(), // JSON string for widget layout and settings
  isDefault: integer("is_default").default(0), // 0 = false, 1 = true (PostgreSQL boolean alternative)
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 6. Task History Table (Audit trail for task changes)
export const ccTaskHistory = pgTable("crm_x_cc_task_history", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  taskId: uuid("task_id").references(() => ccTasks.id, { onDelete: "cascade" }).notNull(),
  changedField: varchar("changed_field", { length: 100 }).notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedBy: uuid("changed_by"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
});

// 7. Task-Deal Links Table (Cross-module integration: link CC tasks to CRM deals)
export const ccTaskDealLinks = pgTable("crm_x_cc_task_deal_links", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  taskId: uuid("task_id").references(() => ccTasks.id, { onDelete: "cascade" }).notNull(),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  linkNotes: text("link_notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 8. Team Members Table (Project Manager settings - configurable team member list)
export const pmTeamMembers = pgTable("crm_x_pm_team_members", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  memberName: varchar("member_name", { length: 100 }).notNull(),
  email: varchar("email", { length: 255 }),
  role: varchar("role", { length: 100 }),
  isActive: integer("is_active").default(1).notNull(), // 1 = active, 0 = inactive
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

// Control Centre Module Schemas
export const insertCcProjectSchema = createInsertSchema(ccProjects).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true
}).extend({
  owner: z.string().nullable().optional().transform(val => val || null),
});
export const updateCcProjectSchema = insertCcProjectSchema.partial(); // createdBy already omitted
export const selectCcProjectSchema = createSelectSchema(ccProjects);

export const insertCcExecutionPlanSchema = createInsertSchema(ccExecutionPlans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true
});
export const updateCcExecutionPlanSchema = insertCcExecutionPlanSchema.partial(); // createdBy already omitted
export const selectCcExecutionPlanSchema = createSelectSchema(ccExecutionPlans);

export const insertCcTaskSchema = createInsertSchema(ccTasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  completedAt: true,
  createdBy: true
}).extend({
  executionPlanId: z.string().nullable().optional().transform(val => val || null),
  assignedTo: z.string().nullable().optional().transform(val => val || null),
});
export const updateCcTaskSchema = insertCcTaskSchema.partial(); // createdBy already omitted
export const selectCcTaskSchema = createSelectSchema(ccTasks);

export const insertCcPerformanceMetricSchema = createInsertSchema(ccPerformanceMetrics).omit({
  id: true,
  createdAt: true,
  recordedAt: true
});
export const updateCcPerformanceMetricSchema = insertCcPerformanceMetricSchema.partial(); // No createdBy field
export const selectCcPerformanceMetricSchema = createSelectSchema(ccPerformanceMetrics);

export const insertCcDashboardConfigurationSchema = createInsertSchema(ccDashboardConfigurations).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateCcDashboardConfigurationSchema = insertCcDashboardConfigurationSchema.partial(); // No createdBy in schema
export const selectCcDashboardConfigurationSchema = createSelectSchema(ccDashboardConfigurations);

export const insertCcTaskHistorySchema = createInsertSchema(ccTaskHistory).omit({
  id: true,
  changedAt: true
});
export const updateCcTaskHistorySchema = insertCcTaskHistorySchema.partial(); // No createdBy field (has changedBy instead)
export const selectCcTaskHistorySchema = createSelectSchema(ccTaskHistory);

export const insertCcTaskDealLinkSchema = createInsertSchema(ccTaskDealLinks).omit({
  id: true,
  createdAt: true,
  createdBy: true
});
export const updateCcTaskDealLinkSchema = insertCcTaskDealLinkSchema.partial(); // createdBy already omitted
export const selectCcTaskDealLinkSchema = createSelectSchema(ccTaskDealLinks);

export const insertPmTeamMemberSchema = createInsertSchema(pmTeamMembers).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updatePmTeamMemberSchema = insertPmTeamMemberSchema.partial();
export const selectPmTeamMemberSchema = createSelectSchema(pmTeamMembers);

// ============================================================================
// TYPES
// ============================================================================

// Control Centre Module Types
export type InsertCcProject = z.infer<typeof insertCcProjectSchema>;
export type UpdateCcProject = z.infer<typeof updateCcProjectSchema>;
export type CcProject = typeof ccProjects.$inferSelect;

export type InsertCcExecutionPlan = z.infer<typeof insertCcExecutionPlanSchema>;
export type UpdateCcExecutionPlan = z.infer<typeof updateCcExecutionPlanSchema>;
export type CcExecutionPlan = typeof ccExecutionPlans.$inferSelect;

export type InsertCcTask = z.infer<typeof insertCcTaskSchema>;
export type UpdateCcTask = z.infer<typeof updateCcTaskSchema>;
export type CcTask = typeof ccTasks.$inferSelect;

export type InsertCcPerformanceMetric = z.infer<typeof insertCcPerformanceMetricSchema>;
export type UpdateCcPerformanceMetric = z.infer<typeof updateCcPerformanceMetricSchema>;
export type CcPerformanceMetric = typeof ccPerformanceMetrics.$inferSelect;

export type InsertCcDashboardConfiguration = z.infer<typeof insertCcDashboardConfigurationSchema>;
export type UpdateCcDashboardConfiguration = z.infer<typeof updateCcDashboardConfigurationSchema>;
export type CcDashboardConfiguration = typeof ccDashboardConfigurations.$inferSelect;

export type InsertCcTaskHistory = z.infer<typeof insertCcTaskHistorySchema>;
export type UpdateCcTaskHistory = z.infer<typeof updateCcTaskHistorySchema>;
export type CcTaskHistory = typeof ccTaskHistory.$inferSelect;

export type InsertCcTaskDealLink = z.infer<typeof insertCcTaskDealLinkSchema>;
export type UpdateCcTaskDealLink = z.infer<typeof updateCcTaskDealLinkSchema>;
export type CcTaskDealLink = typeof ccTaskDealLinks.$inferSelect;

export type InsertPmTeamMember = z.infer<typeof insertPmTeamMemberSchema>;
export type UpdatePmTeamMember = z.infer<typeof updatePmTeamMemberSchema>;
export type PmTeamMember = typeof pmTeamMembers.$inferSelect;
